import { memo, useMemo } from 'react';
import { Draggable, DraggableProvided } from 'react-beautiful-dnd';

import { DataTableCell } from 'components/table/DataTableCell';
import { DataTableRow } from 'components/table/DataTableRow';
import { TableControls } from 'components/table/TableControls';
import { Typography } from 'components/Typography/Typography';
import { usePermissions } from 'hooks/usePermissions';
import { Banner } from 'types/banner';
import { PhotoPaths } from 'types/photo';
import { getImageUrl } from 'utils/getImageUrl';

import s from './BannerTableRow.module.scss';

interface BannerTableRowProps {
  row: Banner;
  index: number;
  onEditRow: (id: number) => void;
  onDeleteRow: (id: number) => void;
}

const BannerTableRow = ({ row, onEditRow, onDeleteRow, index }: BannerTableRowProps) => {
  const { active, title, text, id, images } = row;
  const { can } = usePermissions();

  const popoverButtons = useMemo(
    () => [
      {
        text: 'Редактировать',
        callback: () => onEditRow(id),
      },
      {
        text: 'Удалить',
        access: can('create', 'admin') || can('create', 'moderator'),
        color: '#D32F2F',
        callback: () => onDeleteRow(id),
      },
    ],
    [can, id, onDeleteRow, onEditRow]
  );

  const color = active ? undefined : '#00000061 !important';

  return (
    <Draggable key={`${index}`} draggableId={`dnd-${index}`} index={index}>
      {(draggableProvided: DraggableProvided) => (
        <DataTableRow
          {...draggableProvided.draggableProps}
          {...draggableProvided.dragHandleProps}
          droppableRef={draggableProvided.innerRef}
          sx={{ maxHeight: '104px', height: '104px' }}
          hover
        >
          <DataTableCell>
            <img
              className={s.img}
              src={getImageUrl(images.mobile, PhotoPaths.IMAGE)}
              alt={`img-${index}`}
              loading="lazy"
            />
          </DataTableCell>

          <DataTableCell>
            <Typography sx={{ color }}>{title}</Typography>
          </DataTableCell>

          <DataTableCell>
            <Typography sx={{ color }}>{text}</Typography>
          </DataTableCell>

          <DataTableCell sx={{ width: '56px', paddingLeft: '16px' }}>
            <TableControls id={id} buttons={popoverButtons} />
          </DataTableCell>
        </DataTableRow>
      )}
    </Draggable>
  );
};

export const BannerTableRowMemo = memo(BannerTableRow);
